import { createContext, type PropsWithChildren, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';

import { appConfig } from '@/config';
import { type Me, MeSchema } from '@/entities/me';
import { useAuth } from '@/hooks/use-auth';
import { useWhiteLabelSync } from '@/hooks/use-white-label-sync';
import { usePosthogSyncMe } from '@/posthog/hooks/use-posthog-sync-me';
import { useSentrySyncMe } from '@/sentry/hooks/use-sentry-sync-me';

export type MeProviderProps = PropsWithChildren<{
  loadingComponent: ReactNode;
}>;

export const MeContext = createContext<Me>(
  // @ts-expect-error - This is a placeholder value that should never be used
  null,
);

export function MeProvider({ children, loadingComponent }: MeProviderProps) {
  const auth = useAuth();

  const meQuery = useQuery({
    queryKey: ['me'],
    async queryFn() {
      const accessToken = await auth.getAccessToken();
      // TODO: by openapi client of the bff
      const result = await fetch(`${appConfig.bffUrl}/me`, {
        headers: { authorization: `Bearer ${accessToken}` },
      });

      const { legacyConfig, ...me } = await result.json();

      const config = { ...legacyConfig };

      const user = await MeSchema.parseAsync({
        ...me,
        uuid: '',
        isMfaActive: false,
        config,
      });

      return user;
    },
  });

  useSentrySyncMe(meQuery.data);
  useWhiteLabelSync(meQuery.data);
  usePosthogSyncMe(meQuery.data);

  if (meQuery.isLoading) {
    return <>{loadingComponent}</>;
  }

  if (meQuery.isError || !meQuery.data) {
    throw meQuery.error;
  }

  return (
    <MeContext.Provider value={meQuery.data}>{children}</MeContext.Provider>
  );
}
